import { useEffect } from "react";
import { useAtom } from "jotai";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "motion/react";
import logo from "../../../assets/images/elevintia-logo.svg";
import MenuButton from "../ui/menu-button";
import ScrambleIn from "@/components/ui/fancy/text/scramble-in";
import LiquidGlass from "../ui/liquid-glass";
import { cinematicEase } from "../ui/reveal-text";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import { activeSectionAtom } from "@/atoms/layout.atoms";
import {
  getLockedSection,
  lockSection,
  scrollToSection,
} from "@/lib/scroll-to-section";

export const navSections = [
  { id: "home", label: "Home" },
  { id: "services", label: "Services" },
  { id: "why-us", label: "Why Us" },
  { id: "contact", label: "Contact" },
];

const pathToSection: Record<string, string> = {
  "/": "home",
  "/services": "services",
  "/why-us": "why-us",
  "/contact": "contact",
};

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const hasHardwareAcceleration = useHardwareAcceleration();
  const [activeSection, setActiveSection] = useAtom(activeSectionAtom);

  useEffect(() => {
    setActiveSection(pathToSection[location.pathname] ?? "home");
  }, [location.pathname, setActiveSection]);

  useEffect(() => {
    let frameId: number | null = null;
    const updateActiveSection = () => {
      frameId = null;
      // During a nav-triggered scroll, stay on the destination instead of
      // reporting every section scrolled past.
      const locked = getLockedSection();
      if (locked) {
        setActiveSection(locked);
        return;
      }
      const readingLine = window.innerHeight * 0.34;
      let currentSection = "home";
      const sections = document.querySelectorAll<HTMLElement>(
        isDesktop ? "[data-desktop-section]" : "[data-mobile-section]"
      );

      sections.forEach((section) => {
        if (section.getBoundingClientRect().top <= readingLine) {
          currentSection =
            section.dataset.desktopSection ??
            section.dataset.mobileSection ??
            currentSection;
        }
      });

      setActiveSection(currentSection);
    };

    const handleScroll = () => {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(updateActiveSection);
      }
    };

    updateActiveSection();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
    };
  }, [isDesktop, setActiveSection]);

  const handleSectionClick = (section: string) => {
    const path = section === "home" ? "/" : `/${section}`;
    // Move the highlight straight to the destination, once.
    lockSection(section);
    setActiveSection(section);

    if (location.pathname === path) {
      scrollToSection(section, {
        animate: !reduceMotion && hasHardwareAcceleration,
      });
    } else {
      navigate(path);
    }
  };

  return (
    <motion.header
      initial={reduceMotion ? false : { opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.7, ease: cinematicEase }}
      className="relative z-30 flex h-18 w-full max-w-6xl items-center justify-between px-6 py-3 md:h-24 md:px-0 md:py-3"
    >
      <button
        type="button"
        onClick={() => handleSectionClick("home")}
        aria-label="Elevintia, back to top"
        className="rounded-md"
      >
        <img
          src={logo}
          alt=""
          className="h-8 select-none md:h-10"
          draggable={false}
        />
      </button>

      {/* Mobile menu button */}
      <div className="md:hidden">
        <MenuButton />
      </div>

      {/* Desktop navigation */}
      <LiquidGlass
        as="nav"
        radius="24px"
        aria-label="Primary"
        className="hidden p-2 md:block"
      >
        <div className="flex gap-1">
          {navSections.map((section) => {
            const isActive = activeSection === section.id;

            return (
              <button
                key={section.id}
                type="button"
                aria-current={isActive ? "location" : undefined}
                onClick={() => handleSectionClick(section.id)}
                className={`relative select-none rounded-[20px] px-7 py-3.5 transition-colors duration-300 ${
                  isActive ? "text-white" : "text-white/65 hover:text-white"
                }`}
              >
                {isActive && (
                  <motion.span
                    layoutId="nav-active-pill"
                    transition={{ type: "spring", stiffness: 380, damping: 34 }}
                    className="absolute inset-0 rounded-[20px] bg-white/10 shadow-[inset_3px_3px_0px_-3px_rgba(255,255,255,0.5),inset_-3px_-3px_0px_-3px_rgba(255,255,255,0.3),inset_0_0_2px_0.3px_rgba(255,255,255,0.5)]"
                  />
                )}
                <span className="relative">
                  <ScrambleIn text={section.label} />
                </span>
              </button>
            );
          })}
        </div>
      </LiquidGlass>

    </motion.header>
  );
}
